import type { ComponentType } from 'react';
import { Download, Eye, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import InitialsAvatar from '@/pages/WorkflowV2/components/InitialsAvatar';
import type { EmployeeOption } from '@/pages/WorkflowV2/api';
import { percent, type AssigneeProgress } from '../stats';
import Delta from './Delta';

interface Theme {
  /** Card gradient + border hover, e.g. 'from-emerald-50 hover:border-emerald-300' */
  card: string;
  bar: string;
  track: string;
  text: string;
  button: string;
}

/** Shared neutral look for the Sales Tax and Payroll cards */
export const ASSIGNEE_THEME: Theme = {
  card: 'from-white hover:border-slate-300',
  bar: 'bg-modern-charcoal',
  track: 'bg-slate-200',
  text: 'text-slate-900',
  button: 'bg-modern-charcoal hover:bg-modern-light-charcoal',
};

/** "JANE DOE" (stored casing) → the employee's own spelling, or Title Case */
function displayName(name: string, employees: EmployeeOption[]): string {
  const match = employees.find(e => e.name.toUpperCase() === name);
  if (match) return match.name;
  return name.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

interface AssigneeProgressCardsProps {
  theme: Theme;
  people: AssigneeProgress[];
  total: AssigneeProgress;
  totalLabel: string;
  totalIcon: ComponentType<{ className?: string }>;
  employees: EmployeeOption[];
  loading: boolean;
  /** Last month's progress (people + total) for the "vs last month" line */
  previous?: { people: AssigneeProgress[]; total: AssigneeProgress } | null;
  previousLabel?: string;
  /** Open the Workflow V2 tab, filtered to the employee (null = whole tab) */
  onView: (name: string | null) => void;
  /** Excel export of the employee's accounts; omitted = no Export button */
  onExport?: (name: string | null) => void;
  /** Half-width layout (sharing a row with another section) */
  compact?: boolean;
}

/**
 * One card per assigned employee: Complete / total accounts for the month
 * (the tab's Pending / Complete status), with View + optional Export
 * buttons. Unassigned accounts get their own card so they can't hide.
 */
export default function AssigneeProgressCards({
  theme,
  people,
  total,
  totalLabel,
  totalIcon: TotalIcon,
  employees,
  loading,
  previous,
  previousLabel = 'last month',
  onView,
  onExport,
  compact = false,
}: Readonly<AssigneeProgressCardsProps>) {
  // Completed count last month for a person (null = no comparison)
  const previousComplete = (name: string | null) =>
    previous
      ? (previous.people.find(p => p.name === name)?.complete ?? 0)
      : null;

  const pulse = loading ? 'animate-pulse opacity-40' : '';

  return (
    <div
      className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${compact ? '2xl:grid-cols-3' : 'lg:grid-cols-3 xl:grid-cols-5'}`}
    >
      {people.map(person => {
        const pct = percent(person.complete, person.total);
        const employee = person.name
          ? employees.find(e => e.name.toUpperCase() === person.name)
          : undefined;
        const label = person.name
          ? displayName(person.name, employees)
          : 'Unassigned';
        return (
          <div
            key={person.name ?? '__unassigned__'}
            className={`flex flex-col rounded-xl border border-slate-200 bg-gradient-to-br to-white p-4 shadow-sm transition-all duration-200 hover:shadow-md ${theme.card}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                {person.name ? (
                  <InitialsAvatar
                    name={label}
                    avatarUrl={employee?.avatarUrl}
                    avatarColor={employee?.avatarColor}
                    className="h-7 w-7 text-[11px]"
                  />
                ) : (
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-200">
                    <UserX className="h-4 w-4 text-slate-500" />
                  </span>
                )}
                <span
                  className={`truncate text-sm font-semibold ${theme.text}`}
                >
                  {label}
                </span>
              </div>
              <span
                className={`text-xl font-extrabold tabular-nums ${theme.text} ${pulse}`}
              >
                {person.complete}/{person.total}
              </span>
            </div>
            <div
              className={`mt-3 h-1.5 w-full overflow-hidden rounded-full ${theme.track}`}
            >
              <div
                className={`h-full rounded-full transition-all duration-500 ${theme.bar}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mb-3 mt-1.5 flex flex-wrap items-center justify-between gap-x-2 text-xs text-slate-600">
              <span>
                {pct}% complete
                {person.pending > 0 && (
                  <span className="text-amber-700">
                    {' '}
                    · {person.pending} pending
                  </span>
                )}
              </span>
              <Delta
                current={person.complete}
                previous={previousComplete(person.name)}
                label={previousLabel}
              />
            </p>
            <div className="mt-auto flex gap-1.5 border-t border-slate-100 pt-3">
              {onExport && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onExport(person.name)}
                  disabled={loading || person.total === 0}
                  className="h-7 flex-1 text-xs"
                >
                  <Download className="mr-1 h-3 w-3" />
                  Export
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => onView(person.name)}
                disabled={loading}
                className={`h-7 flex-1 text-xs text-white ${theme.button}`}
                title={
                  person.name
                    ? undefined
                    : 'Opens the whole tab: sort or scan for blank assignees'
                }
              >
                <Eye className="mr-1 h-3 w-3" />
                View
              </Button>
            </div>
          </div>
        );
      })}

      {/* Total */}
      <button
        type="button"
        onClick={() => onView(null)}
        disabled={loading}
        className="flex flex-col rounded-xl border border-slate-300 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-modern-charcoal disabled:cursor-wait"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-200">
              <TotalIcon className="h-4 w-4 text-slate-700" />
            </span>
            <span className="text-sm font-semibold text-slate-900">
              {totalLabel}
            </span>
          </span>
          <span
            className={`text-xl font-extrabold tabular-nums text-slate-900 ${pulse}`}
          >
            {total.complete}/{total.total}
          </span>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className={`h-full rounded-full transition-all duration-500 ${theme.bar}`}
            style={{ width: `${percent(total.complete, total.total)}%` }}
          />
        </div>
        <p className="mt-1.5 flex flex-wrap items-center justify-between gap-x-2 text-xs text-slate-600">
          <span>
            {percent(total.complete, total.total)}% complete
            {total.pending > 0 && <> · {total.pending} pending</>}
          </span>
          <Delta
            current={total.complete}
            previous={previous ? previous.total.complete : null}
            label={previousLabel}
          />
        </p>
        <p className="mt-auto pt-3 text-xs font-medium text-slate-700">
          View all accounts →
        </p>
      </button>
    </div>
  );
}
